package com.mgacreative.hotel.domain.repository

import com.mgacreative.hotel.core.network.SupabaseNetworkClient
import com.mgacreative.hotel.core.tenant.TenantSession
import io.github.jan.supabase.postgrest.postgrest
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.serialization.Serializable

@Serializable
data class PurchaseOrder(
    val id: String = "",
    val tenant_id: String = "",
    val supplier_name: String,
    val status: String = "BEKLIYOR",
    val total_amount: Double = 0.0,
    val created_at: String? = null
)

@Serializable
data class PurchaseOrderItem(
    val id: String = "",
    val order_id: String = "",
    val tenant_id: String = "",
    val item_name: String,
    val category: String = "Diğer",
    val quantity: Int,
    val unit_price: Double = 0.0
)

interface PurchasingRepository {
    fun observeOrders(): Flow<List<PurchaseOrder>>
    fun observeOrderItems(orderId: String): Flow<List<PurchaseOrderItem>>
    suspend fun loadOrders()
    suspend fun loadOrderItems(orderId: String)
    suspend fun createOrder(supplierName: String, items: List<PurchaseOrderItem>)
    suspend fun markOrderAsDelivered(orderId: String)
    suspend fun getOrderItems(orderId: String): List<PurchaseOrderItem>
}

class PurchasingRepositoryImpl(
    private val networkClient: SupabaseNetworkClient,
    private val tenantSession: TenantSession,
    private val inventoryRepository: InventoryRepository
) : PurchasingRepository {

    private val _orders = MutableStateFlow<List<PurchaseOrder>>(emptyList())
    private val _orderItems = MutableStateFlow<List<PurchaseOrderItem>>(emptyList())

    override fun observeOrders(): Flow<List<PurchaseOrder>> = _orders.asStateFlow()
    override fun observeOrderItems(orderId: String): Flow<List<PurchaseOrderItem>> = _orderItems.asStateFlow()

    override suspend fun loadOrders() {
        try {
            val tenantId = tenantSession.currentTenantId
            if (tenantId != null) {
                val result = networkClient.client.postgrest["purchase_orders"]
                    .select {
                        filter { eq("tenant_id", tenantId) }
                    }
                    .decodeList<PurchaseOrder>()
                _orders.value = result.sortedByDescending { it.created_at }
            }
        } catch (e: Exception) {
            e.printStackTrace()
            networkClient.handleError(e)
        }
    }

    override suspend fun loadOrderItems(orderId: String) {
        try {
            val result = networkClient.client.postgrest["purchase_order_items"]
                .select {
                    filter { eq("order_id", orderId) }
                }
                .decodeList<PurchaseOrderItem>()
            _orderItems.value = result
        } catch (e: Exception) {
            e.printStackTrace()
            networkClient.handleError(e)
        }
    }

    override suspend fun createOrder(supplierName: String, items: List<PurchaseOrderItem>) {
        try {
            val tenantId = tenantSession.currentTenantId
            if (tenantId != null) {
                @Serializable
                data class OrderRequest(val tenant_id: String, val supplier_name: String, val status: String, val total_amount: Double)
                
                val totalAmount = items.sumOf { it.quantity * it.unit_price }
                val req = OrderRequest(tenantId, supplierName, "BEKLIYOR", totalAmount)
                
                val insertedOrder = networkClient.client.postgrest["purchase_orders"]
                    .insert(req) { select() }
                    .decodeSingle<PurchaseOrder>()
                
                val itemsToInsert = items.map { 
                    it.copy(order_id = insertedOrder.id, tenant_id = tenantId) 
                }
                
                networkClient.client.postgrest["purchase_order_items"].insert(itemsToInsert)
                
                loadOrders()
            }
        } catch (e: Exception) {
            e.printStackTrace()
            networkClient.handleError(e)
        }
    }

    override suspend fun markOrderAsDelivered(orderId: String) {
        try {
            // Update order status
            @Serializable
            data class StatusUpdate(val status: String)
            
            networkClient.client.postgrest["purchase_orders"]
                .update(StatusUpdate("TESLIM_ALINDI")) {
                    filter { eq("id", orderId) }
                }
                
            // Fetch items in the order
            val itemsInOrder = networkClient.client.postgrest["purchase_order_items"]
                .select { filter { eq("order_id", orderId) } }
                .decodeList<PurchaseOrderItem>()
                
            // Fetch current inventory items to match names
            val tenantId = tenantSession.currentTenantId ?: return
            val inventoryItems = networkClient.client.postgrest["inventory_items"]
                .select { filter { eq("tenant_id", tenantId) } }
                .decodeList<InventoryItem>()
                
            // Add transactions for each item
            itemsInOrder.forEach { orderItem ->
                val matchingInvItem = inventoryItems.find { it.name.equals(orderItem.item_name, ignoreCase = true) }
                if (matchingInvItem != null) {
                    inventoryRepository.addTransaction(
                        itemId = matchingInvItem.id,
                        type = "ADD",
                        quantity = orderItem.quantity,
                        description = "Sipariş Teslimatı"
                    )
                } else {
                    // Create new inventory item if not exists
                    inventoryRepository.addInventoryItem(
                        name = orderItem.item_name,
                        category = orderItem.category,
                        quantity = orderItem.quantity,
                        minThreshold = 5,
                        unit = "Adet"
                    )
                }
            }
            
            
            loadOrders()
        } catch (e: Exception) {
            e.printStackTrace()
            networkClient.handleError(e)
        }
    }

    override suspend fun getOrderItems(orderId: String): List<PurchaseOrderItem> {
        return try {
            networkClient.client.postgrest["purchase_order_items"]
                .select { filter { eq("order_id", orderId) } }
                .decodeList<PurchaseOrderItem>()
        } catch (e: Exception) {
            e.printStackTrace()
            emptyList()
        }
    }
}
